import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../firebase';

export function canonicalizeVestigingId(value) {
  const raw = String(value || '').trim();
  return raw || null;
}

function uniq(list) {
  return Array.from(new Set(list.filter(Boolean)));
}

export function getVestigingIdCandidates(branchId) {
  const raw = String(branchId || '').trim();
  if (!raw) return [];
  return uniq([raw]);
}

function normalizeComparable(v) {
  return String(v || '').trim().toLowerCase();
}

let _branchesCache = null;
let _branchesCacheAt = 0;
let _branchesPromise = null;
const BRANCHES_CACHE_TTL_MS = 60 * 1000;
const RESOLUTION_CACHE_TTL_MS = 5 * 60 * 1000;
const _resolutionCache = new Map();
const _resolutionPromises = new Map();

function getCachedResolution(cacheKey) {
  const hit = _resolutionCache.get(cacheKey);
  if (!hit) return undefined;
  if ((Date.now() - hit.at) > RESOLUTION_CACHE_TTL_MS) {
    _resolutionCache.delete(cacheKey);
    return undefined;
  }
  return hit.value;
}

function setCachedResolution(cacheKey, value) {
  _resolutionCache.set(cacheKey, { value, at: Date.now() });
  return value;
}

async function getBranchesCached() {
  const now = Date.now();
  if (_branchesCache && (now - _branchesCacheAt) < BRANCHES_CACHE_TTL_MS) return _branchesCache;
  if (_branchesPromise) return _branchesPromise;

  _branchesPromise = (async () => {
    try {
      const snap = await getDocs(collection(db, 'branches'));
      _branchesCache = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
      _branchesCacheAt = Date.now();
      return _branchesCache;
    } catch {
      return _branchesCache || [];
    } finally {
      _branchesPromise = null;
    }
  })();

  return _branchesPromise;
}

async function getBranchDerivedCandidates(branchId) {
  const raw = String(branchId || '').trim();
  if (!raw) return [];

  const out = [];

  // 1) Direct branch doc id lookup
  try {
    const snap = await getDoc(doc(db, 'branches', raw));
    if (snap && snap.exists()) {
      const data = snap.data() || {};
      out.push(raw, data.name, data.naam, data.branchId);
    }
  } catch {
    // ignore
  }

  // 2) Fallback: scan branches collection and match on common fields
  try {
    const branches = await getBranchesCached();
    const needle = normalizeComparable(raw);
    const hit = branches.find(b =>
      normalizeComparable(b.id) === needle ||
      normalizeComparable(b.name) === needle ||
      normalizeComparable(b.naam) === needle ||
      normalizeComparable(b.branchId) === needle
    );
    if (hit) {
      out.push(hit.id, hit.name, hit.naam, hit.branchId);
    }
  } catch {
    // ignore
  }

  return uniq(out);
}

async function resolveCanonicalVestigingFromBranches(branchId) {
  const raw = String(branchId || '').trim();
  if (!raw) return null;

  try {
    const branches = await getBranchesCached();
    const needle = normalizeComparable(raw);
    const hit = branches.find((b) => (
      normalizeComparable(b.id) === needle
      || normalizeComparable(b.name) === needle
      || normalizeComparable(b.naam) === needle
      || normalizeComparable(b.branchId) === needle
    ));

    if (!hit?.id) return null;

    const vestigingSnap = await getDoc(doc(db, 'vestigingen', hit.id));
    if (vestigingSnap.exists()) return hit.id;
    return null;
  } catch {
    return null;
  }
}

export async function resolveVestigingDocId(branchId) {
  const raw = String(branchId || '').trim();
  if (!raw) return null;

  const cacheKey = `doc:${normalizeComparable(raw)}`;
  const cached = getCachedResolution(cacheKey);
  if (cached !== undefined) return cached;
  if (_resolutionPromises.has(cacheKey)) return _resolutionPromises.get(cacheKey);

  const resolutionPromise = (async () => {
    let candidates = getVestigingIdCandidates(raw);
    if (candidates.length === 0) return setCachedResolution(cacheKey, null);

    // Enrich candidates with /branches metadata (id/name/naam/branchId)
    // so alias branch IDs can resolve to canonical vestigingen docs.
    try {
      const derived = await getBranchDerivedCandidates(raw);
      candidates = uniq([...candidates, ...derived]);
    } catch {
      // ignore
    }

    const canonicalFromBranches = await resolveCanonicalVestigingFromBranches(raw);
    if (canonicalFromBranches) return setCachedResolution(cacheKey, canonicalFromBranches);

    if (process.env.NODE_ENV !== 'production') {
      console.info('resolveVestigingDocId candidates', { raw, candidates });
    }

    // 1) Exact doc id hit
    for (const candidate of candidates) {
      try {
        const snap = await getDoc(doc(db, 'vestigingen', candidate));
        if (snap.exists()) return setCachedResolution(cacheKey, candidate);
      } catch {
        // ignore
      }
    }

    // 2) Try matching on common fields (name/naam/branchId)
    const fieldNames = ['name', 'naam', 'branchId'];
    for (const candidate of candidates) {
      for (const field of fieldNames) {
        try {
          const q = query(collection(db, 'vestigingen'), where(field, '==', candidate), limit(1));
          const snap = await getDocs(q);
          if (!snap.empty) return setCachedResolution(cacheKey, snap.docs[0].id);
        } catch {
          // ignore
        }
      }
    }

    return setCachedResolution(cacheKey, null);
  })();

  _resolutionPromises.set(cacheKey, resolutionPromise);
  try {
    return await resolutionPromise;
  } finally {
    if (_resolutionPromises.get(cacheKey) === resolutionPromise) _resolutionPromises.delete(cacheKey);
  }
}

async function subcollectionHasRealDocs(vestigingId, subcollectionName) {
  try {
    const snap = await getDocs(query(collection(db, 'vestigingen', vestigingId, subcollectionName), limit(5)));
    if (snap.empty) return false;
    return snap.docs.some(d => !(d.data() || {}).placeholder);
  } catch {
    return false;
  }
}

export async function resolveVestigingIdWithData(branchId, subcollectionName) {
  const raw = String(branchId || '').trim();
  const cacheKey = `data:${normalizeComparable(raw)}:${String(subcollectionName || '').trim()}`;
  const cached = getCachedResolution(cacheKey);
  if (cached !== undefined) return cached;

  const base = await resolveVestigingDocId(raw);
  let candidates = uniq([base, ...getVestigingIdCandidates(raw)]);

  // In data-driven resolution we keep candidates strict to avoid accidental cross-branch bleed.
  candidates = candidates.filter((candidate) => {
    const value = String(candidate || '').trim();
    if (!value) return false;
    return value.length >= 3;
  });

  // Prefer an ID that actually has non-placeholder docs in the desired subcollection.
  const candidateResults = await Promise.all(candidates.map((candidate) => (
    candidate ? subcollectionHasRealDocs(candidate, subcollectionName) : false
  )));
  const matchingCandidateIndex = candidateResults.findIndex(Boolean);
  if (matchingCandidateIndex >= 0) {
    return setCachedResolution(cacheKey, candidates[matchingCandidateIndex]);
  }

  // If nothing matched, try deriving candidates via /branches metadata.
  try {
    const branchCandidates = await getBranchDerivedCandidates(raw);
    candidates = uniq([...candidates, ...branchCandidates]);
    const branchCandidateResults = await Promise.all(branchCandidates.map((candidate) => (
      candidate ? subcollectionHasRealDocs(candidate, subcollectionName) : false
    )));
    const matchingBranchCandidateIndex = branchCandidateResults.findIndex(Boolean);
    if (matchingBranchCandidateIndex >= 0) {
      return setCachedResolution(cacheKey, branchCandidates[matchingBranchCandidateIndex]);
    }
  } catch {
    // ignore
  }

  // Fall back to the best doc-id resolution even if empty.
  return setCachedResolution(cacheKey, base || candidates[0] || null);
}
